import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Text,
  View,
  ScrollView,
  TouchableOpacity,
  Modal,
  ActivityIndicator,
  Alert,
  Image,
  FlatList,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Share,
  InteractionManager,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import FileViewer from 'react-native-file-viewer';
import RNFS from 'react-native-fs';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import { downloadFileWeb } from '../../utils/downloadFile';
import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import TemporaryPasswordBanner from '../../components/TemporaryPasswordBanner/TemporaryPasswordBanner';
import {styles} from './styles';
import {NavigationProps} from '../../navigation/Routes';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import {AppRoutes} from '../../navigation/Routes.types';
import DebtsService from '../../services/DebtsService/DebtsService';
import CardsService from '../../services/CardsService/CardsService';
import {useUserStore} from '../../store/UserStore';
import IoPayService from '../../services/IoPayService/IoPayService';
import PaymentService from '../../services/PaymentService/PaymentService';
import {Images} from '../../constants/Images';
import RNPickerSelect from 'react-native-picker-select';
import {Colors} from '../../constants/Colors';
import {useLoadingStore} from '../../store/LoadingStore';
import OrdersService from '../../services/OrdersService/OrdersService';
import {IDebtsInfo} from '../../types/Debts';
import { showInfo } from '../../utils/globalAlert';

const DebtsPayment = ({route}) => {
  const {items} = route.params;
  const {goBack} = useNavigation();
  const {navigate, replace} = useNavigation<NavigationProps>();
  const navigation = useNavigation();
  const {user} = useUserStore();
  const temporaryPasswordLogin = useUserStore(state => state.temporaryPasswordLogin);
  const [loading, setLoading] = useState<boolean>(false);
  const [pixAvailable, setPixAvailable] = useState<boolean>(false);
  const [creditAvailable, setCreditAvailable] = useState<boolean>(false);
  const [installments, setInstallments] = useState(1);
  const [maxInstallments, setMaxInstallments] = useState(6);

  const [successModalVisible, setSuccessModalVisible] = useState(false);

  const [selectedMethod, setSelectedMethod] = useState<string | null>('');
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [cards, setCards] = useState([]);

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerCep, setCustomerCep] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [customerState, setCustomerState] = useState('');
  const [customerFieldErrors, setCustomerFieldErrors] = useState<
    Record<string, string>
  >({});
  const [loadingCep, setLoadingCep] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [downloadingBoletoFor, setDownloadingBoletoFor] = useState<
    number | null
  >(null);

  const {showLoading, hideLoading} = useLoadingStore();
  const boletoAvailable = items?.some((item: IDebtsInfo) => Boolean(item.barCode));

  const customerModalPendingRef = useRef(false);
  const customerModalPresentingRef = useRef(false);
  const isTransitioningRef = useRef(Platform.OS === 'ios');

  const tryPresentCustomerModal = useCallback(() => {
    if (!customerModalPendingRef.current) return;
    if (customerModalPresentingRef.current) return;
    if (Platform.OS === 'ios' && isTransitioningRef.current) return;

    customerModalPresentingRef.current = true;
    customerModalPendingRef.current = false;

    hideLoading();
    setIsModalVisible(false);

    InteractionManager.runAfterInteractions(() => {
      requestAnimationFrame(() => {
        setShowCustomerModal(true);
      });
    });
  }, [hideLoading]);

  const openCustomerModal = useCallback(() => {
    /**
     * iOS não lida bem com apresentação de Modal durante transição de tela
     * (RNSScreen) ou quando outro Modal (ex.: Loading global) ainda está visível.
     * Então fechamos overlays e apresentamos o modal só após as interações.
     */
    customerModalPendingRef.current = true;
    tryPresentCustomerModal();
  }, [tryPresentCustomerModal]);

  useEffect(() => {
    // quando o modal efetivamente abre/fecha, liberamos a trava de apresentação
    customerModalPresentingRef.current = false;
  }, [showCustomerModal]);

  useEffect(() => {
    // Gatilho determinístico: no iOS, só apresenta após terminar a transição da navegação
    const fallbackTimer =
      Platform.OS === 'ios'
        ? setTimeout(() => {
            isTransitioningRef.current = false;
            tryPresentCustomerModal();
          }, 1500)
        : null;

    const unsubStart =
      // @ts-ignore (tipagem do evento pode variar)
      navigation?.addListener?.('transitionStart', () => {
        isTransitioningRef.current = true;
      });

    const unsubEnd =
      // @ts-ignore (tipagem do evento pode variar)
      navigation?.addListener?.('transitionEnd', () => {
        isTransitioningRef.current = false;
        tryPresentCustomerModal();
      });

    return () => {
      if (typeof unsubStart === 'function') unsubStart();
      if (typeof unsubEnd === 'function') unsubEnd();
      if (fallbackTimer) clearTimeout(fallbackTimer as any);
    };
  }, [navigation, tryPresentCustomerModal]);

  const estadosValidos = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
  ];

  const subtotal = items?.reduce(
    (sum, item) => sum + parseCurrency(item?.docValue),
    0,
  );
  const multa = items?.reduce(
    (sum, item) => sum + parseCurrency(item?.multa),
    0,
  );
  const juros = items?.reduce(
    (sum, item) => sum + parseCurrency(item?.juros),
    0,
  );
  const total = subtotal + multa + juros;

  const installmentOptions = Array.from({length: 6}, (_, i) => {
    const qty = i + 1;
    return {
      qty,
      value: total / qty,
    };
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const clearFieldError = (field: string) => {
    setCustomerFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = {...prev};
      delete next[field];
      return next;
    });
  };

  const handleCepChange = (text: string) => {
    clearFieldError('cep');
    const cleaned = text.replace(/\D/g, '').slice(0, 8);
    let formatted = cleaned;
    if (cleaned.length >= 6) {
      formatted = cleaned.slice(0, 5) + '-' + cleaned.slice(5);
    }
    setCustomerCep(formatted);

    // Se o CEP tem 8 dígitos, consulta a API
    if (cleaned.length === 8) {
      consultarCep(cleaned);
    }
  };

  const consultarCep = async (cepNumeros: string) => {
    setLoadingCep(true);
    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cepNumeros}/json/`,
      );
      const data = await response.json();

      if (data.erro) {
        Alert.alert('CEP não encontrado', 'Verifique o CEP informado');
        return;
      }

      // Preenche automaticamente os campos
      setCustomerAddress(data.logradouro || '');
      setCustomerCity(data.localidade || '');
      setCustomerState(data.uf || '');

      // Se não encontrou o logradouro, foca no campo de endereço para o usuário completar
      if (!data.logradouro) {
        showInfo(
          'Endereço incompleto',
          'CEP encontrado, mas é necessário completar o endereço manualmente',
        );
      }
    } catch (error) {
      console.error('Erro ao consultar CEP:', error);
      Alert.alert(
        'Erro ao consultar CEP',
        'Não foi possível consultar o CEP. Verifique sua conexão com a internet.',
      );
    } finally {
      setLoadingCep(false);
    }
  };

  const createCustomerIfNeeded = async () => {
    try {
      const customer = await CardsService.GetCustomer(user?.clientCode);
      
      if (!customer?.customer) {
        console.log('customer not found');
        return false;
      }
      setCurrentCustomer(customer?.customer || null);

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const formatCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatCNPJ = (cnpj: string) => {
    return cnpj.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5',
    );
  };

  const formatTaxpayerId = (taxpayerId: string) => {
    if (taxpayerId.length === 11) {
      return formatCPF(taxpayerId);
    } else if (taxpayerId.length === 14) {
      return formatCNPJ(taxpayerId);
    }
    return taxpayerId;
  };

  const handleCreateCustomer = async () => {
    if (!customerName.trim()) {
      setCustomerFieldErrors({name: 'Por favor, preencha o nome.'});
      Alert.alert('Erro', 'Por favor, preencha o nome.');
      return;
    }

    if (!customerEmail.trim()) {
      setCustomerFieldErrors({email: 'Por favor, preencha o email.'});
      Alert.alert('Erro', 'Por favor, preencha o email.');
      return;
    }

    if (!validateEmail(customerEmail)) {
      setCustomerFieldErrors({email: 'Por favor, insira um email válido.'});
      Alert.alert('Erro', 'Por favor, insira um email válido.');
      return;
    }

    if (!customerCep || !customerAddress || !customerCity || !customerState) {
      setCustomerFieldErrors({
        ...(!customerCep && {cep: 'Campo obrigatório.'}),
        ...(!customerAddress && {address: 'Campo obrigatório.'}),
        ...(!customerCity && {city: 'Campo obrigatório.'}),
        ...(!customerState && {state: 'Campo obrigatório.'}),
      });
      Alert.alert('Erro', 'Preencha todos os campos obrigatórios.');
      return;
    }

    if (!estadosValidos.includes(customerState)) {
      setCustomerFieldErrors({state: 'Estado inválido.'});
      Alert.alert('Erro', 'Estado inválido.');
      return;
    }

    setCustomerFieldErrors({});

    const client = user?.current_client;

    if (!client) {
      Alert.alert('Erro', 'Cliente não encontrado.');
      return;
    }

    try {
      setLoading(true);

      const data = {
        client_code: user.clientCode,
        first_name: customerName.trim(),
        email: customerEmail.trim().toLowerCase(),
        taxpayer_id: formatTaxpayerId(client.cnpj_cpf),
        address: {
          city: customerCity,
          state: customerState,
          line1: customerAddress,
        },
      };

      const customer = await CardsService.CreateCustomer(data);
      setShowCustomerModal(false);

      setCurrentCustomer(customer?.customer || null);

      GetMethods();
    } catch (error) {
      Alert.alert('Erro', 'Não foi possível criar o customer.');
    } finally {
      setLoading(false);
    }
  };

  function parseCurrency(value: string | number): number {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    const normalized = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(normalized) || 0;
  }

  function formatCurrency(value: number): string {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    });
  }

  const handleSelectMethod = (method: string, card?: any) => {
    setSelectedMethod(method);
    if (method === 'credit_card' && card) {
      setSelectedCard(card);
    } else {
      setSelectedCard(null);
    }
    setIsModalVisible(false);
  };

  const GetMethods = useCallback(async () => {
    setLoading(true);
    let didShowGlobalLoading = false;
    try {
      const hasCustomer = await createCustomerIfNeeded();
      console.log('hasCustomer:', hasCustomer);
      if (!hasCustomer) {
        openCustomerModal();
        return;
      }

      showLoading();
      didShowGlobalLoading = true;

      const response = await DebtsService.GetPaymentMethods();

      if (response) {
        const activeMethods = response.data.filter(
          method => method.is_active === 1 && method.usage === 'debits',
        );

        const pixAvailable = activeMethods.some(
          method => method.name === 'pix',
        );
        const creditAvailable = activeMethods.some(
          method => method.name === 'credit_card',
        );

        setPixAvailable(pixAvailable);
        setCreditAvailable(creditAvailable);

        if (creditAvailable) {
          let creditSuccess = false;
          try {
            const token = await authenticate();

            console.log('Token de autenticação:', token);

            const cardsResponse = await CardsService.GetCards(
              user?.clientCode,
              token,
            );
            console.log('cardsResponse:', cardsResponse);

            const cards = cardsResponse.cards;
            setMaxInstallments(
              cardsResponse.installments ? cardsResponse.installments : 6,
            );

            const cardImages: {[key: string]: any} = {
              Visa: Images.visaIcon,
              MasterCard: Images.masterCardIcon,
            };

            const mappedCards = cards.items.map((card: any) => ({
              id: card.id_card,
              brand: card.card_brand,
              type: 'Crédito',
              lastDigits: card.last4_digits,
              isFavorite: cards.default_debit?.id_card === card.id_card,
              image: cardImages[card.card_brand] || '',
            }));

            setCards(mappedCards);

            if (mappedCards.length > 0) {
              const favoriteCard =
                mappedCards.find(card => card.isFavorite) || mappedCards[0];

              setSelectedMethod('credit_card');
              setSelectedCard(favoriteCard);
              creditSuccess = true;
            }
          } catch (error) {
            console.error('Erro ao carregar cartões:', error);
          }

          if (!creditSuccess && pixAvailable) {
            setSelectedMethod('pix');
            setSelectedCard(null);
          } else if (!creditSuccess && boletoAvailable) {
            setSelectedMethod('boleto');
            setSelectedCard(null);
          }
        } else if (pixAvailable) {
          setSelectedMethod('pix');
          setSelectedCard(null);
        } else if (boletoAvailable) {
          setSelectedMethod('boleto');
          setSelectedCard(null);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      if (didShowGlobalLoading) {
        hideLoading();
      }
    }
  }, [user?.clientCode, boletoAvailable, openCustomerModal, showLoading, hideLoading]);

  const authenticate = async () => {
    try {
      const response = await IoPayService.IoPayAuth();

      return response.access_token;
    } catch (error) {
      Alert.alert(
        'Ops...',
        'Houve um erro ao se comunicar com a api de pagamentos, tente novamente em alguns instantes!',
      );
    }
  };

  useFocusEffect(
    useCallback(() => {
      GetMethods();
    }, [GetMethods]),
  );

  const processPayment = async () => {
    showLoading();
    setLoading(true);
    const referenceIds =
      'D:' +
      items
        .map(item => `${item.reference_id}${item.docYear}${item.branch}`)
        .join(',');

    if (selectedMethod === 'pix') {
      try {
        const totalAmount = items.reduce((sum, item) => {
          return sum + item.docValue + item.juros + item.multa;
        }, 0);

        const totalAmountFormatted = totalAmount.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        const data = {
          client_code: user?.clientCode,
          reference_type: 'debt',
          reference_id: referenceIds,
          amount: totalAmountFormatted,
        };

        const response = await PaymentService.PixDebitPayment(data);

        const item = response;

        replace('DebtsPaymentPix', {item});
      } catch (error) {
        setLoading(false);
        hideLoading();

        let message = 'Não foi possível processar o pagamento.';
        const response = error?.response?.data;

        if (response) {
          if (typeof response.message === 'string') {
            message = response.message;
          }

          if (typeof response.errors === 'string') {
            message += `\n\n${response.errors}`;
          }

          if (typeof response.errors === 'object') {
            const flatErrors = Object.values(response.errors).flat().join('\n');
            message += `\n\n${flatErrors}`;
          }
        }

        Alert.alert('Erro', message);
      } finally {
        hideLoading();
        setLoading(false);
      }
    } else if (selectedMethod === 'credit_card') {
      const itemsFormatted = items.map(item => ({
        name: item.order,
        code: item.reference_id,
        quantity: 1,
        amount: item.docValue + item.juros + item.multa,
      }));

      const totalAmount = items.reduce((sum, item) => {
        return sum + item.docValue + item.juros + item.multa;
      }, 0);

      const totalAmountFormatted = totalAmount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const data = {
        client_code: user?.clientCode,
        card_id: selectedCard.id,
        reference_id: referenceIds,
        amount: totalAmountFormatted,
        products: itemsFormatted,
        installments,
      };

      try {
        const response = await PaymentService.CreditCardDebitPayment(data);

        if (response?.status === 'succeeded' || response?.success === true) {
          setSuccessModalVisible(true);
        } else {
          const errorMsg =
            response?.message || 'Não foi possível processar o pagamento.';
          Alert.alert('Erro', errorMsg);
        }
      } catch (error) {
        setLoading(false);
        hideLoading();

        let message = 'Não foi possível processar o pagamento.';
        const response = error?.response?.data;

        if (response) {
          if (typeof response.message === 'string') {
            message = response.message;
          }

          if (typeof response.errors === 'string') {
            message += `\n\n${response.errors}`;
          }

          if (typeof response.errors === 'object') {
            const flatErrors = Object.values(response.errors).flat().join('\n');
            message += `\n\n${flatErrors}`;
          }
        }

        Alert.alert('Erro', message);
      } finally {
        setLoading(false);
        hideLoading();
      }
    }
  };

  const handleCopyLineDigit = (line?: string) => {
    if (!line) {
      Alert.alert(
        'Linha digitável indisponível',
        'Não foi possível encontrar o código do boleto.',
      );
      return;
    }

    Clipboard.setString(line.replace(/\s/g, ''));
    Alert.alert(
      'Linha digitável copiada',
      'Agora você pode colar a linha digitável no aplicativo do banco.',
    );
  };

  const openFileWithDialog = async (filePath: string) => {
    try {
      await FileViewer.open(filePath);
    } catch (error: any) {
      console.error('Erro ao abrir o arquivo:', error);
      if (error?.message?.includes('No app')) {
        try {
          await FileViewer.open(filePath, {showOpenWithDialog: true});
        } catch (dialogError) {
          console.error('Erro ao abrir o diálogo:', dialogError);
          Alert.alert(
            'Erro',
            'Nenhum aplicativo disponível para abrir este tipo de arquivo.',
          );
        }
      } else {
        Alert.alert('Erro', 'Falha ao abrir o arquivo.');
      }
    }
  };

  const handleDownloadDebtBoleto = async (debt: IDebtsInfo) => {
    if (!debt?.chaveNf) {
      Alert.alert(
        'Boleto indisponível',
        'Não foi possível localizar a chave do boleto (CHVNF) para este débito.',
      );
      return;
    }

    try {
      setDownloadingBoletoFor(debt.id);
      const response = await OrdersService.downloadBoletos(debt.chaveNf);

      if (!response) {
        throw new Error('Boleto não encontrado');
      }

      const base64data = typeof response === 'string'
        ? (response.includes(',') ? response.split(',')[1] : response)
        : response;
      const reference = debt.reference_id ?? debt.id;
      const fileName = `boleto_${reference}_${Date.now()}.pdf`;

      if (Platform.OS === 'web') {
        downloadFileWeb(base64data, fileName);
      } else {
        const directoryPath =
          Platform.OS === 'android'
            ? RNFS.DownloadDirectoryPath
            : RNFS.DocumentDirectoryPath;
        const filePath = `${directoryPath}/${fileName}`;
        await RNFS.writeFile(filePath, base64data, 'base64');

        if (Platform.OS === 'ios') {
          await Share.share({url: `file://${filePath}`});
        } else {
          await openFileWithDialog(filePath);
        }
      }
    } catch (error: any) {
      console.error('Erro ao baixar boleto:', error);
      Alert.alert('Erro', 'Falha ao baixar o boleto.');
    } finally {
      setDownloadingBoletoFor(null);
    }
  };

  const renderItem = ({item}: any) => (
    <TouchableOpacity onPress={() => handleSelectMethod('credit_card', item)}>
      <View style={styles.card}>
        {item.image && <Image source={item.image} style={styles.cardImage} />}
        <View style={styles.cardInfo}>
          <Text style={styles.cardTitle}>{`${item.brand} - ${item.type}`}</Text>
          <Text style={styles.cardNumber}>**** {item.lastDigits}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <Header />
      <View style={styles.spaceHeader}>
        <SearchHeader onPress={goBack} title="Pagamento" mb={20} />
      </View>
      <TemporaryPasswordBanner />

      {loading && (
        <View style={{padding: 16}}>
          <ActivityIndicator size="large" color="#000" />
        </View>
      )}

      <ScrollView
        style={{flex: 1}}
        contentContainerStyle={{paddingBottom: 120}}>
        <Text style={styles.sectionTitle}>Forma de pagamento</Text>
        <View style={styles.paymentRow}>
          {selectedMethod === 'credit_card' && selectedCard && (
            <>
              {selectedCard.image && (
                <Image source={selectedCard.image} style={styles.cardImage} />
              )}
              <View style={styles.cardInfo}>
                <Text style={styles.cardTitle}>
                  {`${selectedCard.brand} - ${selectedCard.type}`}
                </Text>
                <Text style={styles.cardNumber}>
                  **** {selectedCard.lastDigits}
                </Text>
              </View>
            </>
          )}
          {selectedMethod === 'pix' && (
            <>
              <Image source={Images.pixIcon} style={styles.cardImage} />
              <View style={styles.cardInfo}>
                <Text style={styles.cardTitle}>Pix</Text>
              </View>
            </>
          )}
          {selectedMethod === 'boleto' && (
            <>
              <Image source={Images.boletoIcon} style={styles.cardImage} />
              <View style={styles.cardInfo}>
                <Text style={styles.cardTitle}>Boleto</Text>
                <Text style={styles.selectedMethod}>
                  Linha digitável disponível abaixo
                </Text>
              </View>
            </>
          )}
          <TouchableOpacity
            onPress={() => {
              if (currentCustomer) {
                setIsModalVisible(true);
              } else {
                openCustomerModal();
              }
            }}
            disabled={loading}>
            <Text style={styles.changeButton}>Trocar</Text>
          </TouchableOpacity>
        </View>

        {selectedMethod === 'credit_card' && (
          <View style={{marginHorizontal: 16, marginTop: 10, marginBottom: 10}}>
            <Text style={{fontWeight: '600', marginBottom: 6}}>
              Parcelamento
            </Text>
            <View
              style={{
                borderWidth: 1,
                borderColor: '#ccc',
                borderRadius: 8,
                overflow: 'hidden',
              }}>
              <RNPickerSelect
                onValueChange={value => {
                  if (typeof value === 'number') {
                    setInstallments(value);
                  }
                }}
                value={installments}
                items={Array.from({length: maxInstallments}, (_, i) => {
                  const qty = i + 1;
                  return {
                    label: `${qty}x de ${formatCurrency(total / qty)}`,
                    value: qty,
                    key: `${qty}`,
                  };
                })}
                useNativeAndroidPickerStyle={false}
                placeholder={{}}
                Icon={() => (
                  <FontAwesome name="chevron-down" size={16} color="#555" />
                )}
                style={pickerSelectStyles}
              />
            </View>
          </View>
        )}

        {selectedMethod === 'boleto' && boletoAvailable && (
          <View style={styles.boletoSection}>
            <Text style={styles.sectionTitle}>Linha digitável</Text>
            <View style={styles.boletoList}>
              {items.map((item: IDebtsInfo, index: number) => {
                const line = item.barCode;
                const key = item.id ?? `boleto-${index}`;
                return (
                  <View key={key} style={styles.boletoLineCard}>
                    <Text style={styles.boletoLineTitle}>
                      {item.name || `Débito ${index + 1}`}
                    </Text>
                    <Text style={styles.boletoLineText}>
                      {line || 'Linha digitável não disponível'}
                    </Text>
                    <View style={styles.boletoActions}>
                      <ButtonComponent
                        title="Copiar linha digitável"
                        outlined
                        onPress={() => handleCopyLineDigit(line)}
                        disabled={!line}
                        width="48%"
                      />
                      <ButtonComponent
                        title="Baixar boleto (.pdf)"
                        outlined
                        onPress={() => handleDownloadDebtBoleto(item)}
                        isLoading={downloadingBoletoFor === item.id}
                        disabled={!item.chaveNf}
                        width="48%"
                      />
                    </View>
                  </View>
                );
              })}
            </View>
            <Text style={styles.boletoWarningText}>
              Pagamentos via boleto podem levar até 48 horas para serem
              processados. Realize seu pagamento com PIX ou cartão de crédito
              para liberação do limite mais rápida e faça já seus novos pedidos!
            </Text>
          </View>
        )}

        <Text style={styles.sectionTitle}>Débitos ({items.length})</Text>
        <View style={{paddingHorizontal: 16}}>
          {items.map((item, index) => (
            <View
              key={index}
              style={{
                backgroundColor: '#f8f8f8',
                padding: 12,
                borderRadius: 8,
                marginBottom: 8,
              }}>
              <Text style={{fontWeight: '600', fontSize: 14, color: '#222'}}>
                {item?.name || `Débito ${index + 1}`}
              </Text>
              <Text style={{fontSize: 14, color: '#555'}}>
                Valor:{' '}
                {formatCurrency(
                  parseCurrency(item?.docValue) +
                    parseCurrency(item?.juros) +
                    parseCurrency(item?.multa),
                )}
              </Text>
            </View>
          ))}
        </View>

        <Text style={styles.sectionTitle}>Resumo</Text>
        <View style={styles.summaryBox}>
          <View style={styles.summaryLine}>
            <Text style={styles.label}>Subtotal:</Text>
            <Text style={styles.value}>{formatCurrency(subtotal)}</Text>
          </View>
          <View style={styles.summaryLine}>
            <Text style={styles.label}>Multa:</Text>
            <Text style={styles.value}>{formatCurrency(multa)}</Text>
          </View>
          <View style={styles.summaryLine}>
            <Text style={styles.label}>Juros:</Text>
            <Text style={styles.value}>{formatCurrency(juros)}</Text>
          </View>
          <View style={styles.summaryLine}>
            <Text style={[styles.label, {fontWeight: 'bold'}]}>Total:</Text>
            <Text style={[styles.value, {fontWeight: 'bold'}]}>
              {formatCurrency(total)}
            </Text>
          </View>
        </View>
      </ScrollView>

      {selectedMethod && selectedMethod !== 'boleto' && (
        <View style={styles.buttonWrapper}>
          <View style={styles.buttonBackground} />
          <ButtonComponent
            title={`Pagar`}
            onPress={processPayment}
            width="90%"
            disabled={loading || temporaryPasswordLogin}
          />
        </View>
      )}

      <Modal visible={isModalVisible} transparent animationType="slide">
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              Escolha uma forma de pagamento
            </Text>
            {pixAvailable && (
              <TouchableOpacity onPress={() => handleSelectMethod('pix', null)}>
                <View style={styles.pixCard}>
                  <Image source={Images.pixIcon} style={styles.cardImage} />
                  <View style={styles.cardInfo}>
                    <Text style={styles.cardTitle}>Pix</Text>
                  </View>
                </View>
              </TouchableOpacity>
            )}
            {boletoAvailable && (
              <TouchableOpacity
                onPress={() => handleSelectMethod('boleto', null)}>
                <View style={styles.pixCard}>
                  <Image source={Images.boletoIcon} style={styles.cardImage} />
                  <View style={styles.cardInfo}>
                    <Text style={styles.cardTitle}>Boleto</Text>
                    <Text style={styles.boletoOptionDescription}>
                      Copie a linha digitável ou baixe o PDF para pagamento offline.
                    </Text>
                  </View>
                </View>
              </TouchableOpacity>
            )}
            {creditAvailable && (
              <>
                <FlatList
                  data={cards}
                  renderItem={renderItem}
                  keyExtractor={item => item.id}
                  contentContainerStyle={styles.listContent}
                />

                <TouchableOpacity
                  style={{
                    backgroundColor: Colors.RedPallete.red,
                    padding: 12,
                    borderRadius: 8,
                    alignItems: 'center',
                    marginTop: 10,
                  }}
                  onPress={() => {
                    setIsModalVisible(false);
                    navigate(AppRoutes.NEW_CARD);
                  }}>
                  <Text style={{color: '#fff', fontWeight: '600'}}>
                    + Novo cartão
                  </Text>
                </TouchableOpacity>
              </>
            )}

            <TouchableOpacity
              onPress={() => setIsModalVisible(false)}
              style={{marginTop: 12}}>
              <Text style={{textAlign: 'center', color: '#666'}}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={successModalVisible} transparent animationType="fade">
        <View style={styles.modalBackdrop}>
          <View style={styles.successModalContent}>
            <Text style={styles.modalTitle}>
              Pagamento realizado com sucesso!
            </Text>
            <Text
              style={{textAlign: 'center', color: '#555', marginVertical: 12}}>
              Seus débitos foram pagos com sucesso.
            </Text>
            <View style={{width: '100%'}}>
              <ButtonComponent
                title="Continuar"
                width="100%"
                onPress={() => {
                  setSuccessModalVisible(false);
                  replace(AppRoutes.DEBTS);
                }}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* MODAL DE CUSTOMER CORRIGIDA */}
      <Modal
        visible={showCustomerModal}
        transparent
        animationType="slide"
        presentationStyle={Platform.OS === 'ios' ? 'overFullScreen' : 'fullScreen'}
        onRequestClose={() => setShowCustomerModal(false)}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.modalBackdrop}>
          <TouchableOpacity
            activeOpacity={1}
            style={styles.customerModalContainer}
            onPress={() => {}}>
            <View style={styles.customerModalContent}>
              <ScrollView
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled">
                <Text style={styles.modalTitle}>
                  Complete seus dados para o primeiro pagamento
                </Text>

                <TextInput
                  style={[
                    styles.input,
                    customerFieldErrors.name && {
                      borderColor: 'red',
                      borderWidth: 1,
                    },
                  ]}
                  placeholder="Nome completo"
                  placeholderTextColor="#999"
                  value={customerName}
                  onChangeText={text => {
                    setCustomerName(text);
                    clearFieldError('name');
                  }}
                />
                {customerFieldErrors.name && (
                  <Text
                    style={{
                      color: 'red',
                      marginTop: -8,
                      marginBottom: 8,
                      fontSize: 12,
                    }}>
                    {customerFieldErrors.name}
                  </Text>
                )}

                <TextInput
                  style={[
                    styles.input,
                    customerFieldErrors.email && {
                      borderColor: 'red',
                      borderWidth: 1,
                    },
                  ]}
                  placeholder="Email"
                  placeholderTextColor="#999"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  value={customerEmail}
                  onChangeText={text => {
                    setCustomerEmail(text);
                    clearFieldError('email');
                  }}
                />
                {customerEmail && !validateEmail(customerEmail) && (
                  <Text
                    style={{
                      color: 'red',
                      marginTop: -8,
                      marginBottom: 8,
                      fontSize: 12,
                    }}>
                    Email inválido
                  </Text>
                )}
                {customerFieldErrors.email &&
                  !(customerEmail && !validateEmail(customerEmail)) && (
                    <Text
                      style={{
                        color: 'red',
                        marginTop: -8,
                        marginBottom: 8,
                        fontSize: 12,
                      }}>
                      {customerFieldErrors.email}
                    </Text>
                  )}

                <View style={{position: 'relative'}}>
                  <TextInput
                    style={[
                      styles.input,
                      customerFieldErrors.cep && {
                        borderColor: 'red',
                        borderWidth: 1,
                      },
                    ]}
                    placeholder="CEP"
                    placeholderTextColor="#999"
                    keyboardType="numeric"
                    maxLength={9}
                    value={customerCep}
                    onChangeText={handleCepChange}
                  />
                  {loadingCep && (
                    <View style={{position: 'absolute', right: 12, top: 12}}>
                      <ActivityIndicator size="small" color="#666" />
                    </View>
                  )}
                </View>
                {customerFieldErrors.cep && (
                  <Text
                    style={{
                      color: 'red',
                      marginTop: -8,
                      marginBottom: 8,
                      fontSize: 12,
                    }}>
                    {customerFieldErrors.cep}
                  </Text>
                )}

                <TextInput
                  style={[
                    styles.input,
                    customerFieldErrors.address && {
                      borderColor: 'red',
                      borderWidth: 1,
                    },
                  ]}
                  placeholder="Endereço"
                  placeholderTextColor="#999"
                  value={customerAddress}
                  onChangeText={text => {
                    setCustomerAddress(text);
                    clearFieldError('address');
                  }}
                />
                {customerFieldErrors.address && (
                  <Text
                    style={{
                      color: 'red',
                      marginTop: -8,
                      marginBottom: 8,
                      fontSize: 12,
                    }}>
                    {customerFieldErrors.address}
                  </Text>
                )}
                <TextInput
                  style={[
                    styles.input,
                    customerFieldErrors.city && {
                      borderColor: 'red',
                      borderWidth: 1,
                    },
                  ]}
                  placeholder="Cidade"
                  placeholderTextColor="#999"
                  value={customerCity}
                  onChangeText={text => {
                    setCustomerCity(text);
                    clearFieldError('city');
                  }}
                />
                {customerFieldErrors.city && (
                  <Text
                    style={{
                      color: 'red',
                      marginTop: -8,
                      marginBottom: 8,
                      fontSize: 12,
                    }}>
                    {customerFieldErrors.city}
                  </Text>
                )}
                <TextInput
                  style={[
                    styles.input,
                    customerFieldErrors.state && {
                      borderColor: 'red',
                      borderWidth: 1,
                    },
                  ]}
                  placeholder="Estado (UF)"
                  placeholderTextColor="#999"
                  value={customerState}
                  onChangeText={text => {
                    setCustomerState(text.toUpperCase());
                    clearFieldError('state');
                  }}
                  maxLength={2}
                />
                {customerState && !estadosValidos.includes(customerState) && (
                  <Text
                    style={{
                      color: 'red',
                      marginTop: -8,
                      marginBottom: 8,
                      fontSize: 12,
                    }}>
                    UF inválido
                  </Text>
                )}
                {customerFieldErrors.state &&
                  !(
                    customerState && !estadosValidos.includes(customerState)
                  ) && (
                    <Text
                      style={{
                        color: 'red',
                        marginTop: -8,
                        marginBottom: 8,
                        fontSize: 12,
                      }}>
                      {customerFieldErrors.state}
                    </Text>
                  )}

                <ButtonComponent
                  disabled={loading || loadingCep}
                  title="Salvar"
                  onPress={() => {
                    if (!loading && !loadingCep) handleCreateCustomer();
                  }}
                />

                <TouchableOpacity
                  onPress={() => {
                    if (!loading && !loadingCep) {
                      setShowCustomerModal(false);
                      replace(AppRoutes.DEBTS);
                    }
                  }}>
                  <Text
                    style={{
                      textAlign: 'center',
                      color: '#666',
                      marginTop: 12,
                      paddingBottom: 20,
                    }}>
                    Cancelar
                  </Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

export default DebtsPayment;

const pickerSelectStyles = StyleSheet.create({
  inputIOS: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    color: '#555',
    fontSize: 16,
    paddingRight: 32,
  },
  inputAndroid: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    color: '#555',
    fontSize: 16,
    paddingRight: 32,
  },
  inputAndroidContainer: {
    minHeight: 44,
    justifyContent: 'center',
  },
  inputIOSContainer: {
    minHeight: 44,
    justifyContent: 'center',
  },
  placeholder: {
    color: '#999',
  },
  iconContainer: {
    top: 14,
    right: 12,
  },
});